import { useQuery } from '@tanstack/react-query';

interface BuildVersion {
  buildVersion: string;
}

/**
 * Use native `fetch` instead of api.get(). `version.json` is a static asset
 * served next to the app, not the Jane API. `api` also adds custom headers,
 * which would trigger a CORS preflight when partner-hosted.
 */
const fetchBuildVersion = async (versionUrl: string): Promise<BuildVersion> => {
  const response = await fetch(versionUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${versionUrl}: ${response.status}`);
  }
  return response.json();
};

interface UseBuildVersionParams {
  /** The version this client was built with; also the initial data */
  currentVersion: string;
  pollingIntervalMs: number;
  versionUrl: string;
}

/**
 * Polls the deployed build version. Seeded with `currentVersion` so nothing is
 * fetched on mount, and polling stops once a different version is seen.
 */
export const useBuildVersion = ({
  currentVersion,
  pollingIntervalMs,
  versionUrl,
}: UseBuildVersionParams) =>
  useQuery({
    initialData: { buildVersion: currentVersion },
    queryFn: () => fetchBuildVersion(versionUrl),
    queryKey: ['buildVersion', versionUrl],
    refetchInterval: ({ state: { data } }) =>
      data?.buildVersion === currentVersion ? pollingIntervalMs : false,
    refetchIntervalInBackground: false,
    staleTime: pollingIntervalMs,
  });
